import { isNameCharacter } from "../names/character-name.ts";

/**
 * The family lookup's pattern (ADR-056's note of 2026-10-08 on the lookup): what a GM types to find a family by its
 * name or by one of its characters' first names. `*` stands for any letters and may lead, trail or both; between it
 * stand at least `LOOKUP_MIN_CHARS` and at most `LOOKUP_MAX_CHARS` characters a name may hold. Matching ignores case,
 * as names are unique ignoring case. The gateway matches with a `LIKE` over the lower-case name (`likePattern`); the
 * page and the tests' doubles match with `matchesLookup`, which agrees with it.
 */

/**
 * The fewest characters besides the `*`: the shortest whole name.
 */
export const LOOKUP_MIN_CHARS = 3;

/**
 * The most characters besides the `*`: the longest name.
 */
export const LOOKUP_MAX_CHARS = 12;

/**
 * The most hits one answer holds.
 */
export const LOOKUP_HITS = 50;

/**
 * How the characters typed are matched: the whole name, its start, its end, or anywhere in it.
 */
export type LookupMode = "exact" | "prefix" | "suffix" | "contains";

/**
 * A lookup held to its rule: its mode and the characters besides the `*`, in lower case.
 */
export interface Lookup {
  readonly mode: LookupMode;
  readonly literal: string;
}

/**
 * One hit: a family, or a character with the family it belongs to.
 */
export interface LookupHit {
  readonly kind: "family" | "character";
  readonly name: string;
  readonly family: string;
}

/**
 * The answer of `GET /moderation/lookup`: at most `LOOKUP_HITS` hits, sorted, and how many were found in all.
 */
export interface LookupAnswer {
  readonly hits: readonly LookupHit[];
  readonly total: number;
}

/**
 * Reads what a GM typed.
 *
 * @param text The text as typed; spaces round it are dropped.
 * @returns The lookup, or null when a `*` stands anywhere but the start or the end, fewer than 3 or more than 12
 *   characters stand besides it, or a character is one no name holds.
 * @example
 * parseLookup("*ant");
 * // { mode: "suffix", literal: "ant" }
 */
export function parseLookup(text: string): Lookup | null {
  const trimmed = text.trim();
  const leads = trimmed.startsWith("*");
  const trails = trimmed.length > 1 && trimmed.endsWith("*");
  const literal = trimmed.slice(leads ? 1 : 0, trails ? -1 : undefined);
  if (literal.length < LOOKUP_MIN_CHARS || literal.length > LOOKUP_MAX_CHARS || [...literal].some((character) => !isNameCharacter(character))) {
    return null;
  }
  const mode: LookupMode = leads && trails ? "contains" : leads ? "suffix" : trails ? "prefix" : "exact";
  return { mode, literal: literal.toLowerCase() };
}

/**
 * The `LIKE` pattern for a lookup, to be matched against the lower-case name with `ESCAPE '\'`: `\`, `%` and `_`
 * escaped, then `%` where the `*` stood.
 *
 * @param lookup The lookup.
 * @returns The pattern.
 * @example
 * likePattern({ mode: "prefix", literal: "a_b" });
 * // "a\\_b%"
 */
export function likePattern(lookup: Lookup): string {
  const escaped = lookup.literal.replace(/[\\%_]/g, (character) => `\\${character}`);
  return `${lookup.mode === "suffix" || lookup.mode === "contains" ? "%" : ""}${escaped}${lookup.mode === "prefix" || lookup.mode === "contains" ? "%" : ""}`;
}

/**
 * Whether a name matches a lookup, ignoring case, as the gateway's `LIKE` does.
 *
 * @param lookup The lookup.
 * @param name The name.
 * @returns True on a match.
 */
export function matchesLookup(lookup: Lookup, name: string): boolean {
  const lower = name.toLowerCase();
  switch (lookup.mode) {
    case "exact":
      return lower === lookup.literal;
    case "prefix":
      return lower.startsWith(lookup.literal);
    case "suffix":
      return lower.endsWith(lookup.literal);
    case "contains":
      return lower.includes(lookup.literal);
  }
}
